/**
 * The one StandardMaterial3D decode (ADR-0031), for an inline `[sub_resource]` and an
 * external `.tres` alike: raw property strings in, `StandardMaterial3DData` out. Pure,
 * with no `three`, React or I/O. A texture reference stays a raw string, since resolving
 * it needs the tables of the file the material arrived in.
 */
import { parseColorOrUndefined } from '../../../utils/colorParser';
import { sRGBToLinearRGB } from '../../../utils/colorSpace';
import { boolOr, enumOr, floatOr, intOr, vec3Or } from '../../../parser/valueParsers';
import { parseVector3 } from '../../../parser/vectors';
import { drawsInShadowPass } from '../../../godot/rendering/shadowPass';
import { TEXTURE_CHANNELS, TextureChannel } from '../../../godot/rendering/materials/textureChannel';
import { DiffuseMode } from '../../../godot/rendering/materials/diffuseMode';
import { SpecularMode } from '../../../godot/rendering/materials/specularMode';
import { DetailUV } from '../../../godot/rendering/materials/detailUv';
import { StencilCompare, StencilFlag, StencilMode } from '../../../godot/rendering/materials/stencil';
import { BASE_MATERIAL_3D_DEFAULTS, EMISSION_OPERATOR_DEFAULT } from '../../../godot/scene/baseMaterial3D';
import { clamp } from '../../../godot/math/math';
import { AlphaAntiAliasing, hashAntialiasing, scissorAntialiasing, } from '../../../godot/rendering/materials/alphaAntialiasing';
import { emissionScalars } from './emission';
import { BillboardMode } from '../../../godot/rendering/materials/billboardMode';
import { BlendMode } from '../../../godot/rendering/materials/blendMode';
import { CullMode } from '../../../godot/rendering/materials/cullMode';
import { DistanceFadeMode } from '../../../godot/rendering/materials/distanceFadeMode';
import { DepthDrawMode } from '../../../godot/rendering/materials/depthDrawMode';
import { ShadingMode } from '../../../godot/rendering/materials/shadingMode';
import { Transparency } from '../../../godot/rendering/materials/transparency';
import { HEIGHTMAP_DEFAULTS, PARTICLES_ANIM_DEFAULTS, TEXTURE_SLOTS, } from './types';
const CONTEXT = 'StandardMaterial3D';
const TRANSPARENCY_MODES = [
    Transparency.DISABLED,
    Transparency.ALPHA,
    Transparency.ALPHA_SCISSOR,
    Transparency.ALPHA_HASH,
    Transparency.ALPHA_DEPTH_PRE_PASS,
];
const BLEND_MODES = [
    BlendMode.MIX,
    BlendMode.ADD,
    BlendMode.SUB,
    BlendMode.MUL,
    BlendMode.PREMULT_ALPHA,
];
const CULL_MODES = [CullMode.BACK, CullMode.FRONT, CullMode.DISABLED];
const DEPTH_DRAW_MODES = [DepthDrawMode.OPAQUE_ONLY, DepthDrawMode.ALWAYS, DepthDrawMode.DISABLED];
/**
 * The feature flag Godot gates each texture slot behind. A gated slot applies only when
 * its flag is on, as `_update_shader` emits the sampler only inside its
 * `if (features[…])` branch. An ungated slot applies whenever it is authored.
 */
const SLOT_GATES = {
    albedo_texture: null,
    normal_texture: 'normal_enabled',
    roughness_texture: null,
    metallic_texture: null,
    emission_texture: 'emission_enabled',
    ao_texture: 'ao_enabled',
    heightmap_texture: 'heightmap_enabled',
    anisotropy_flowmap: 'anisotropy_enabled',
};
export function decodeStandardMaterial3D(properties) {
    const albedo = parseColorOrUndefined(properties['albedo_color']);
    // Godot stores sRGB and its `source_color` uniform converts to linear before the
    // shader, as three's `color` expects. Not clamped: `albedo_color` has no range hint,
    // `Color::srgb_to_linear` extrapolates past 1, and an HDR albedo such as
    // `Color(2.33575, 3.29442, 3.29442, 1)` crosses the glow bright pass on purpose.
    const albedoLinear = albedo ? sRGBToLinearRGB(albedo.r, albedo.g, albedo.b) : null;
    const emissionEnabled = boolOr(properties['emission_enabled'], false, CONTEXT);
    const emission = emissionScalars(parseColorOrUndefined(properties['emission']), floatOr(properties['emission_energy_multiplier'], 1, CONTEXT), emissionEnabled);
    // Godot's flag-gated features. An omitted scalar is the constructor's default, as
    // Godot never writes one, and a feature whose flag is off reads as three's off-state (0).
    // Refraction rewrites the depth-draw mode and the alpha, so the pass decision
    // below depends on it.
    const clearcoatEnabled = boolOr(properties['clearcoat_enabled'], false, CONTEXT);
    const rimEnabled = boolOr(properties['rim_enabled'], false, CONTEXT);
    const heightmapEnabled = boolOr(properties['heightmap_enabled'], false, CONTEXT);
    const anisotropyEnabled = boolOr(properties['anisotropy_enabled'], false, CONTEXT);
    const refractionEnabled = boolOr(properties['refraction_enabled'], false, CONTEXT);
    const triplanar = decodeTriplanar(properties);
    // `_update_shader` emits `ALPHA *= albedo.a * albedo_tex.a` only when
    // `transparency != TRANSPARENCY_DISABLED` (or shadow-to-opacity, pixel-alpha
    // distance fade or proximity fade is on). So albedo alpha < 1 with transparency
    // DISABLED renders fully opaque in Godot.
    const transparency = enumOr(properties['transparency'], Transparency.DISABLED, TRANSPARENCY_MODES, `${CONTEXT}.transparency`);
    const blendMode = enumOr(properties['blend_mode'], BlendMode.MIX, BLEND_MODES, `${CONTEXT}.blend_mode`);
    // Which of Godot's two render lists this surface joins, and whether its fragments
    // reach the depth buffer. `transparency` is only one of the inputs.
    const depthTest = !boolOr(properties['no_depth_test'], false, CONTEXT);
    // `_update_shader`: `DepthDrawMode ddm = depth_draw_mode; if
    // (features[FEATURE_REFRACTION]) { ddm = DEPTH_DRAW_ALWAYS; }`. A refractive
    // surface draws depth wherever it lands.
    const depthDrawMode = refractionEnabled
        ? DepthDrawMode.ALWAYS
        : enumOr(properties['depth_draw_mode'], DepthDrawMode.OPAQUE_ONLY, DEPTH_DRAW_MODES, `${CONTEXT}.depth_draw_mode`);
    const scissorThreshold = clamp(floatOr(properties['alpha_scissor_threshold'], BASE_MATERIAL_3D_DEFAULTS.alphaScissorThreshold, CONTEXT), 0, 1);
    const alphaAntialiasing = decodeAlphaAntialiasing(properties, transparency, scissorThreshold);
    const alphaFlags = shaderAlphaFlags(properties, transparency, refractionEnabled, alphaAntialiasing !== null);
    const membership = alphaPassMembership(alphaFlags, {
        blendMode,
        refractionEnabled,
        depthDrawMode,
        depthTest,
    });
    const { alphaPass, depthInAlphaPass } = membership;
    const castsShadow = drawsInShadowPass(membership);
    const normalScale = floatOr(properties['normal_scale'], BASE_MATERIAL_3D_DEFAULTS.normalScale, CONTEXT);
    // FEATURE_REFRACTION replaces the `ALPHA *= albedo.a * albedo_tex.a` line with
    // `ALPHA = 1.0` (`material.cpp:1832-1837`), so a refractive surface is fully opaque however
    // low its authored alpha. A shader that writes no ALPHA never reads the albedo alpha either.
    const readsAlbedoAlpha = alphaFlags.usesAlpha && !refractionEnabled;
    return {
        albedo: albedoLinear ?? [1, 1, 1],
        alpha: readsAlbedoAlpha && albedo ? clamp(albedo.a, 0, 1) : 1,
        metallic: clamp(floatOr(properties['metallic'], BASE_MATERIAL_3D_DEFAULTS.metallic, CONTEXT), 0, 1),
        roughness: clamp(floatOr(properties['roughness'], BASE_MATERIAL_3D_DEFAULTS.roughness, CONTEXT), 0, 1),
        // `material.cpp:2160-2163`: the setter checks no range.
        specular: floatOr(properties['metallic_specular'], BASE_MATERIAL_3D_DEFAULTS.specular, CONTEXT),
        emission,
        emissionOperator: intOr(properties['emission_operator'], EMISSION_OPERATOR_DEFAULT, CONTEXT),
        textureFilter: intOr(properties['texture_filter'], BASE_MATERIAL_3D_DEFAULTS.textureFilter, CONTEXT),
        textureRepeat: boolOr(properties['texture_repeat'], true, CONTEXT),
        uv1Scale: vec2FromVector3(properties['uv1_scale'], { x: 1, y: 1 }),
        uv1Offset: vec2FromVector3(properties['uv1_offset'], { x: 0, y: 0 }),
        ...uv2Transform(properties, triplanar?.uv2 != null),
        uv2Slots: uv2Slots(properties),
        transparency,
        transparent: alphaPass,
        castsShadow,
        // `alpha_scissor_threshold` hint is "0,1,0.001". 0 means "no cutout" to three,
        // which every non-scissor mode wants. A refractive surface's ALPHA is 1.0, which no
        // threshold cuts, while three would cut on the texture alpha Godot never reads. Under
        // alpha antialiasing the ramp cuts at the threshold itself.
        alphaTest: transparency === Transparency.ALPHA_SCISSOR && readsAlbedoAlpha && !alphaAntialiasing
            ? scissorThreshold
            : 0,
        // `scene_forward_clustered.glsl:1398-1411`: the hash cuts the ALPHA the shader writes.
        alphaHash: transparency === Transparency.ALPHA_HASH && readsAlbedoAlpha,
        alphaHashScale: floatOr(properties['alpha_hash_scale'], BASE_MATERIAL_3D_DEFAULTS.alphaHashScale, CONTEXT),
        alphaAntialiasing,
        depthDrawMode,
        depthWrite: godotDepthWrite(alphaPass, depthDrawMode, depthTest),
        alphaPassDepthWrite: godotDepthWrite(true, depthDrawMode, depthTest),
        depthInAlphaPass,
        readsAlbedoAlpha,
        // `scene_forward_clustered.glsl:1413-1415`.
        opaqueAfterCut: alphaFlags.usesAlphaClip && !alphaFlags.usesAlphaAntialiasing,
        depthTest,
        blendMode,
        cullMode: enumOr(properties['cull_mode'], CullMode.BACK, CULL_MODES, `${CONTEXT}.cull_mode`),
        cullModeExplicit: properties['cull_mode'] !== undefined,
        shadingMode: intOr(properties['shading_mode'], ShadingMode.PER_PIXEL, `${CONTEXT}.shading_mode`) ===
            ShadingMode.UNSHADED
            ? 'unshaded'
            : 'per_pixel',
        // `material.cpp:2462-2468`: the setter checks no range.
        diffuseMode: intOr(properties['diffuse_mode'], DiffuseMode.DIFFUSE_BURLEY, CONTEXT),
        specularMode: intOr(properties['specular_mode'], SpecularMode.SPECULAR_SCHLICK_GGX, CONTEXT),
        useVertexColors: boolOr(properties['vertex_color_use_as_albedo'], false, CONTEXT),
        vertexColorIsSrgb: boolOr(properties['vertex_color_is_srgb'], false, CONTEXT),
        disablesSpecularOcclusion: boolOr(properties['disable_specular_occlusion'], false, CONTEXT),
        // The shader reads `ao_light_affect` only with `ao_enabled` (`material.cpp:1959`).
        aoLightAffect: boolOr(properties['ao_enabled'], false, CONTEXT)
            ? floatOr(properties['ao_light_affect'], BASE_MATERIAL_3D_DEFAULTS.aoLightAffect, CONTEXT)
            : 0,
        aoTextureChannel: textureChannel(properties, 'ao_texture_channel'),
        roughnessTextureChannel: textureChannel(properties, 'roughness_texture_channel'),
        metallicTextureChannel: textureChannel(properties, 'metallic_texture_channel'),
        normalScale: { x: normalScale, y: normalScale },
        triplanar,
        // Triplanar mapping of UV1 overrides height mapping (`material.cpp:1541`).
        heightmap: heightmapEnabled && !triplanar?.uv1 ? decodeHeightmap(properties) : null,
        clearcoat: clearcoatEnabled
            ? clamp(floatOr(properties['clearcoat'], BASE_MATERIAL_3D_DEFAULTS.clearcoat, CONTEXT), 0, 1)
            : 0,
        clearcoatRoughness: clearcoatEnabled
            ? clamp(floatOr(properties['clearcoat_roughness'], BASE_MATERIAL_3D_DEFAULTS.clearcoatRoughness, CONTEXT), 0, 1)
            : 0,
        rim: rimEnabled ? clamp(floatOr(properties['rim'], BASE_MATERIAL_3D_DEFAULTS.rim, CONTEXT), 0, 1) : 0,
        rimTint: rimEnabled
            ? clamp(floatOr(properties['rim_tint'], BASE_MATERIAL_3D_DEFAULTS.rimTint, CONTEXT), 0, 1)
            : 0,
        usesPointSize: boolOr(properties['use_point_size'], false, CONTEXT),
        pointSize: floatOr(properties['point_size'], BASE_MATERIAL_3D_DEFAULTS.pointSize, CONTEXT),
        billboardMode: intOr(properties['billboard_mode'], BillboardMode.BILLBOARD_DISABLED, CONTEXT),
        billboardKeepScale: boolOr(properties['billboard_keep_scale'], false, CONTEXT),
        particlesAnim: {
            hFrames: intOr(properties['particles_anim_h_frames'], PARTICLES_ANIM_DEFAULTS.hFrames, CONTEXT),
            vFrames: intOr(properties['particles_anim_v_frames'], PARTICLES_ANIM_DEFAULTS.vFrames, CONTEXT),
            loop: boolOr(properties['particles_anim_loop'], PARTICLES_ANIM_DEFAULTS.loop, CONTEXT),
        },
        anisotropy: anisotropyEnabled
            ? clamp(floatOr(properties['anisotropy'], BASE_MATERIAL_3D_DEFAULTS.anisotropy, CONTEXT), -1, 1)
            : 0,
        // Godot refraction is a screen-space distortion; three models the same
        // "see the background through this surface" effect volumetrically, so
        // `refraction_scale` drives `thickness` and cannot go negative there.
        transmission: refractionEnabled ? 1 : 0,
        refractionThickness: refractionEnabled
            ? Math.max(0, floatOr(properties['refraction_scale'], BASE_MATERIAL_3D_DEFAULTS.refractionScale, CONTEXT))
            : 0,
        textureSlots: decodeTextureSlots(properties),
        nextPass: properties['next_pass'],
        grows: boolOr(properties['grow'], false, CONTEXT),
        growAmount: floatOr(properties['grow_amount'], BASE_MATERIAL_3D_DEFAULTS.growAmount, CONTEXT),
        readsStencil: readsStencilOf(properties),
    };
}
/**
 * `render_forward_clustered.cpp:4099-4114` reads a link's stencil when its mode is on,
 * its flags carry READ and its compare is not ALWAYS; the generated shader emits the
 * block only with a mode and flags (`material.cpp:916-968`). A write-only link, and an
 * ALWAYS one, draw without a stencil value, so they are not readers. This previewer
 * requests no stencil buffer, so a reader here draws nothing; the decision itself stays
 * in the decode, the only place a raw bag is read (ADR-0031).
 */
function readsStencilOf(properties) {
    const mode = intOr(properties['stencil_mode'], StencilMode.DISABLED, CONTEXT);
    // The bit field is an INT slot declared at int64 (`material.cpp:3267,3776`), the
    // width the validator reads it at.
    const flags = intOr(properties['stencil_flags'], 0, CONTEXT, 'int64');
    const compare = intOr(properties['stencil_compare'], StencilCompare.ALWAYS, CONTEXT);
    return (mode !== StencilMode.DISABLED && (flags & StencilFlag.READ) !== 0 && compare !== StencilCompare.ALWAYS);
}
/** A `*_texture_channel` enum, red by default (`material.cpp:3985-3987`). */
function textureChannel(properties, property) {
    return enumOr(properties[property], TextureChannel.TEXTURE_CHANNEL_RED, TEXTURE_CHANNELS, `${CONTEXT}.${property}`);
}
/** What `BaseMaterial3D::_update_shader` emits for alpha, which every alpha decision reads. */
function shaderAlphaFlags(properties, transparency, refractionEnabled, usesAlphaAntialiasing) {
    const proximityFade = boolOr(properties['proximity_fade_enabled'], false, CONTEXT);
    const distanceFadeMode = intOr(properties['distance_fade_mode'], DistanceFadeMode.DISABLED, CONTEXT);
    const shadowToOpacity = boolOr(properties['shadow_to_opacity'], false, CONTEXT);
    // The shader writes ALPHA: for a transparency mode, for refraction (`ALPHA = 1.0`),
    // and for the fades and `shadow_to_opacity`, which share one `else if`.
    const usesAlpha = refractionEnabled ||
        transparency !== Transparency.DISABLED ||
        shadowToOpacity ||
        distanceFadeMode === DistanceFadeMode.PIXEL_ALPHA ||
        proximityFade;
    // The two cutout modes discard instead of blending, so they stay opaque.
    const usesAlphaClip = transparency === Transparency.ALPHA_SCISSOR || transparency === Transparency.ALPHA_HASH;
    // `depth_prepass_alpha` is emitted for ALPHA_DEPTH_PRE_PASS alone (`material.cpp:898`).
    const usesDepthPrepassAlpha = transparency === Transparency.ALPHA_DEPTH_PRE_PASS;
    return { usesAlpha, usesAlphaClip, usesAlphaAntialiasing, usesDepthPrepassAlpha, proximityFade };
}
/**
 * The cutout's alpha antialiasing. `ALPHA_ANTIALIASING_EDGE` is written for any mode but OFF, and
 * only under a cutout (`material.cpp:1844-1847`). The edge defaults to 0.3 (`material.cpp:3977`).
 */
function decodeAlphaAntialiasing(properties, transparency, scissorThreshold) {
    // `set_alpha_antialiasing` stores any value (`material.cpp:2395-2401`).
    const mode = intOr(properties['alpha_antialiasing_mode'], AlphaAntiAliasing.ALPHA_ANTIALIASING_OFF, CONTEXT);
    const edge = floatOr(properties['alpha_antialiasing_edge'], 0.3, CONTEXT);
    if (transparency === Transparency.ALPHA_SCISSOR)
        return scissorAntialiasing(mode, scissorThreshold, edge);
    if (transparency === Transparency.ALPHA_HASH)
        return hashAntialiasing(mode, edge);
    return null;
}
/**
 * Godot's `ShaderData::uses_alpha_pass()` and `uses_depth_in_alpha_pass()`
 * (`scene_shader_forward_clustered.h:279-293`), in its own variable names so the two diff
 * line by line. `transparency` alone never decides either.
 */
function alphaPassMembership(alphaFlags, inputs) {
    const { usesAlpha, usesAlphaClip, usesAlphaAntialiasing, usesDepthPrepassAlpha, proximityFade } = alphaFlags;
    const { blendMode, refractionEnabled, depthDrawMode, depthTest } = inputs;
    // Refraction samples `screen_texture`, and refraction or proximity fade samples
    // `depth_texture`.
    const hasReadScreenAlpha = refractionEnabled || proximityFade;
    const hasBaseAlpha = (usesAlpha && (!usesAlphaClip || usesAlphaAntialiasing)) || hasReadScreenAlpha;
    // `uses_blend_alpha` is true for ADD, SUB, MUL and PREMULT_ALPHA, which puts an
    // additive material in the alpha pass whatever its `transparency` says.
    const hasAlpha = hasBaseAlpha || blendMode !== BlendMode.MIX;
    const noDepthDraw = depthDrawMode === DepthDrawMode.DISABLED;
    const noDepthTest = !depthTest;
    return {
        alphaPass: hasAlpha || hasReadScreenAlpha || noDepthDraw || noDepthTest,
        depthInAlphaPass: (usesDepthPrepassAlpha || usesAlphaAntialiasing) && !(noDepthDraw || noDepthTest),
    };
}
/**
 * The colour pass's `enable_depth_write` (`scene_shader_forward_clustered.cpp`
 * `_create_pipeline`): the depth-draw mode decides it, and the transparent pipeline
 * overrides OPAQUE_ONLY to false. A disabled depth test writes nothing.
 */
function godotDepthWrite(alphaPass, depthDrawMode, depthTest) {
    if (!depthTest || depthDrawMode === DepthDrawMode.DISABLED)
        return false;
    if (!alphaPass)
        return true;
    // A depth prepass writes the depth of a `depth_prepass_alpha` surface, not its colour pipeline.
    return depthDrawMode !== DepthDrawMode.OPAQUE_ONLY;
}
/**
 * The authored texture references, minus every slot whose feature flag is off. Gated
 * here, not per arrival path, so a material has the same maps however it is referenced.
 */
function decodeTextureSlots(properties) {
    const slots = {};
    for (const slot of TEXTURE_SLOTS) {
        const reference = properties[slot];
        if (reference === undefined)
            continue;
        const gate = SLOT_GATES[slot];
        if (gate !== null && !boolOr(properties[gate], false, CONTEXT))
            continue;
        slots[slot] = reference;
    }
    return slots;
}
/**
 * `uv2_scale` and `uv2_offset` on the UV2 attribute, where Godot applies them: under a UV2 detail
 * layer that does not project (`material.cpp:1249-1256`). Elsewhere the shader reads UV2 as stored.
 */
function uv2Transform(properties, isProjected) {
    const identity = { uv2Scale: { x: 1, y: 1 }, uv2Offset: { x: 0, y: 0 } };
    const detailUv = intOr(properties['detail_uv_layer'], DetailUV.DETAIL_UV_1, CONTEXT);
    if (isProjected || detailUv !== DetailUV.DETAIL_UV_2)
        return identity;
    return {
        uv2Scale: vec2FromVector3(properties['uv2_scale'], identity.uv2Scale),
        uv2Offset: vec2FromVector3(properties['uv2_offset'], identity.uv2Offset),
    };
}
/**
 * The slots that sample UV2: emission under `emission_on_uv2` (`material.cpp:1772-1776`) and
 * AO under `ao_on_uv2` (:1942-1946).
 */
function uv2Slots(properties) {
    const slots = [];
    if (boolOr(properties['emission_on_uv2'], false, CONTEXT))
        slots.push('emission_texture');
    if (boolOr(properties['ao_on_uv2'], false, CONTEXT))
        slots.push('ao_texture');
    return slots;
}
/**
 * Godot writes UV transforms as `Vector3`, and only x and y reach a 2D UV. It wraps the
 * throwing `parseVector3`, so the float grammar, scientific notation included, is shared.
 */
function vec2FromVector3(raw, fallback) {
    if (!raw)
        return fallback;
    try {
        const { x, y } = parseVector3(raw);
        return { x, y };
    }
    catch {
        return fallback;
    }
}
/**
 * The triplanar mapping of each UV set that projects, or null for none. `uv1_world_triplanar`
 * picks the space of UV1's projection and of the tangent frame (`material.cpp:1386-1435`).
 */
function decodeTriplanar(properties) {
    const uv1 = decodeTriplanarProjection(properties, 'uv1');
    const uv2 = decodeTriplanarProjection(properties, 'uv2');
    if (!uv1 && !uv2)
        return null;
    return { uv1, uv2, isTangentWorld: boolOr(properties['uv1_world_triplanar'], false, CONTEXT) };
}
/** The projection of UV set `uv` under `<uv>_triplanar`, or null where the set reads its attribute. */
function decodeTriplanarProjection(properties, uv) {
    if (!boolOr(properties[`${uv}_triplanar`], false, CONTEXT))
        return null;
    const sharpness = floatOr(properties[`${uv}_triplanar_sharpness`], BASE_MATERIAL_3D_DEFAULTS.triplanarSharpness, CONTEXT);
    return {
        scale: vec3Or(properties[`${uv}_scale`], { x: 1, y: 1, z: 1 }, `${CONTEXT}.${uv}_scale`),
        offset: vec3Or(properties[`${uv}_offset`], { x: 0, y: 0, z: 0 }, `${CONTEXT}.${uv}_offset`),
        // `set_uv1_triplanar_blend_sharpness` and its UV2 twin clamp (`material.cpp:2786`, `:2814`).
        sharpness: clamp(sharpness, 0, 150),
        isWorld: boolOr(properties[`${uv}_world_triplanar`], false, CONTEXT),
    };
}
/** Height mapping over Godot's defaults. The setters check no range. */
function decodeHeightmap(properties) {
    const defaults = HEIGHTMAP_DEFAULTS;
    const sign = (key) => (boolOr(properties[key], false, CONTEXT) ? -1 : 1);
    return {
        scale: floatOr(properties['heightmap_scale'], defaults.scale, CONTEXT),
        isDeep: boolOr(properties['heightmap_deep_parallax'], defaults.isDeep, CONTEXT),
        layers: [
            intOr(properties['heightmap_min_layers'], defaults.layers[0], CONTEXT),
            intOr(properties['heightmap_max_layers'], defaults.layers[1], CONTEXT),
        ],
        flip: [sign('heightmap_flip_tangent'), sign('heightmap_flip_binormal')],
        readsHeight: boolOr(properties['heightmap_flip_texture'], defaults.readsHeight, CONTEXT),
    };
}
//# sourceMappingURL=decode.js.map